import { useEffect } from 'react';
import { useAuthStore } from '@/features/auth/state/auth-store';
import { LoginScreen } from '@/features/auth/components/LoginScreen';
import { AuthenticatedShell } from './AuthenticatedShell';
import { Loading } from './Loading';
import { NotFound } from './NotFound';
import { navigate, useCurrentPath } from './router';
import { SharedPresentationPage } from '@/features/shared-view/SharedPresentationPage';

const KNOWN_STATIC_PATHS = new Set([
  '/',
  '/login',
  '/app',
  '/app/profile',
  '/app/templates',
  '/app/trash',
  '/app/admin',
]);
const FUNNEL_ROUTE_PATTERN = /^\/app\/funnels\/[^/]+$/;
const PRESENTATION_ROUTE_PATTERN = /^\/app\/funnels\/[^/]+\/present$/;
const DOCUMENTATION_ROUTE_PATTERN = /^\/app\/funnels\/[^/]+\/documentation$/;
const SHARED_ROUTE_PATTERN = /^\/s\/([^/]+)$/;

function isKnownPath(path: string): boolean {
  return (
    KNOWN_STATIC_PATHS.has(path) ||
    FUNNEL_ROUTE_PATTERN.test(path) ||
    PRESENTATION_ROUTE_PATTERN.test(path) ||
    DOCUMENTATION_ROUTE_PATTERN.test(path) ||
    SHARED_ROUTE_PATTERN.test(path)
  );
}

/**
 * SPEC-002 §56/§58: bootstrap via /api/auth/me, then route by auth status.
 * Server-side authorization remains the real authority (plan §57) — this
 * only improves UX.
 */
export function App() {
  const status = useAuthStore((state) => state.status);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const path = useCurrentPath();
  const sharedMatch = path.match(SHARED_ROUTE_PATTERN);

  useEffect(() => {
    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 'unknown') return;

    const isAppRoute = path.startsWith('/app');

    if (status === 'authenticated' && (path === '/' || path === '/login')) {
      navigate('/app');
    } else if (status === 'unauthenticated' && isAppRoute) {
      navigate('/login');
    } else if (status === 'unauthenticated' && path === '/') {
      navigate('/login');
    }
  }, [status, path]);

  if (!isKnownPath(path)) {
    return <NotFound />;
  }

  if (sharedMatch) {
    return <SharedPresentationPage token={sharedMatch[1]!} />;
  }

  if (status === 'unknown') {
    return <Loading />;
  }

  if (status === 'authenticated' && path.startsWith('/app')) {
    return <AuthenticatedShell path={path} />;
  }

  if (status === 'unauthenticated' && !path.startsWith('/app')) {
    return <LoginScreen />;
  }

  // Between-render redirect gap (e.g. authenticated user still on /login for
  // one tick): show a controlled loading state instead of the wrong screen.
  return <Loading />;
}
